<script lang="ts" setup>
  import { useId } from 'vue';

  const props = withDefaults(
    defineProps<{
      label: string;
      decreaseLabel: string;
      increaseLabel: string;
      min: number;
      max: number;
      actionName: string;
      step?: number;
      unit?: string;
      error?: string;
    }>(),
    { step: 1, unit: '', error: '' },
  );
  const value = defineModel<number>({ required: true });

  const labelId = useId();
  const errorId = useId();

  const stepDown = () => {
    value.value = Math.max(props.min, (Math.ceil(value.value / props.step) - 1) * props.step);
  };

  const stepUp = () => {
    value.value = Math.min(props.max, (Math.floor(value.value / props.step) + 1) * props.step);
  };
</script>

<template>
  <div :data-invalid="error ? '' : undefined" class="stepper">
    <span :id="labelId" class="label">{{ label }}</span>
    <div :aria-describedby="error ? errorId : undefined" :aria-labelledby="labelId" class="controls" role="group">
      <span v-if="step > 1" aria-hidden="true" class="step">−{{ step }}</span>
      <button
        :aria-label="decreaseLabel"
        :data-faro-user-action-name="actionName"
        :disabled="value <= min"
        class="step-button"
        type="button"
        @click="stepDown"
      >
        -
      </button>
      <output class="value">{{ value }}{{ unit }}</output>
      <button
        :aria-label="increaseLabel"
        :data-faro-user-action-name="actionName"
        :disabled="value >= max"
        class="step-button"
        type="button"
        @click="stepUp"
      >
        +
      </button>
      <span v-if="step > 1" aria-hidden="true" class="step">+{{ step }}</span>
    </div>
    <p v-if="error" :id="errorId" class="error" role="alert">{{ error }}</p>
  </div>
</template>

<style scoped>
  .stepper {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: var(--control-max-width);
    width: 100%;
  }

  .label {
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
    letter-spacing: 0.1rem;
    padding-inline-start: 0.1rem;
    text-transform: uppercase;
  }

  .controls {
    align-items: center;
    display: flex;
    gap: 1rem;
  }

  .step-button {
    align-items: center;
    background-color: var(--color-primary);
    border: none;
    border-radius: var(--radius-full);
    color: var(--color-on-primary);
    cursor: pointer;
    display: flex;
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-strong);
    justify-content: center;
    min-height: 2.75rem;
    min-width: 2.75rem;
    transition: transform 0.1s;
  }

  .step-button:active {
    transform: scale(0.9);
  }

  .step-button:disabled {
    background-color: var(--color-disabled);
    cursor: not-allowed;
  }

  .value {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-strong);
    min-width: 4.5rem;
    text-align: center;
  }

  .step {
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
  }

  .error {
    color: var(--color-error);
    font-size: var(--font-size-sm);
    line-height: 1.5;
    margin: 0;
    text-align: center;
  }

  [data-invalid] .value {
    color: var(--color-error);
  }
</style>
